import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import kbOshaSafetyStandards from "@/assets/kb-osha-safety-standards.jpg";
import kbPpeGuidelines from "@/assets/kb-ppe-guidelines.jpg";
import kbHazardousMaterial from "@/assets/kb-hazardous-material.jpg";
import kbEmergencyResponse from "@/assets/kb-emergency-response.jpg";
import kbFallProtection from "@/assets/kb-fall-protection.jpg";
import kbSafetyAudits from "@/assets/kb-safety-audits.jpg";
import kbSafetyTraining from "@/assets/kb-safety-training.jpg";
import kbIncidentReporting from "@/assets/kb-incident-reporting.jpg";
import solAiSafetyMonitoring from "@/assets/sol-ai-safety-monitoring.jpg";
import solWearableSafety from "@/assets/sol-wearable-safety.jpg";
import solDigitalSafetyPlatform from "@/assets/sol-digital-safety-platform.jpg";
import solVrSafetyTraining from "@/assets/sol-vr-safety-training.jpg";
import kbPrefabricationFramework from "@/assets/kb-prefabrication-framework.jpg";
import kbSafetyProtocolSystem from "@/assets/kb-safety-protocol-system.jpg";
import kbProjectManagementFramework from "@/assets/kb-project-management-framework.jpg";
import kbGreenBuildingSystem from "@/assets/kb-green-building-system.jpg";
import kbBimIntegrationToolkit from "@/assets/kb-bim-integration-toolkit.jpg";
import kbEngineeringDesignStandards from "@/assets/kb-engineering-design-standards.jpg";
import kbConstructionDataAnalytics from "@/assets/kb-construction-data-analytics.jpg";
import kbCurtOwnersBlueprint from "@/assets/kb-curt-owners-blueprint.jpg";
import kbOwnersSafetyBlueprint from "@/assets/kb-owners-safety-blueprint.jpg";
import ogDrillingOptimization from "@/assets/og-drilling-optimization.jpg";
import ogPipelineIntegrity from "@/assets/og-pipeline-integrity.jpg";
import ogReservoirSimulation from "@/assets/og-reservoir-simulation.jpg";
import ogRefineryAutomation from "@/assets/og-refinery-automation.jpg";
import ogCarbonCapture from "@/assets/og-carbon-capture.jpg";
import ogExplorationAi from "@/assets/og-exploration-ai.jpg";
import ogTransportationFleet from "@/assets/og-transportation-fleet.jpg";
import ogWellCompletion from "@/assets/og-well-completion.jpg";
import ogQualityControl from "@/assets/og-quality-control.jpg";
import ogStorageSolutions from "@/assets/og-storage-solutions.jpg";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Search, Filter, Download, PlusCircle, CheckSquare, ListFilter } from "lucide-react";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Checkbox } from "@/components/ui/checkbox";
import IspaiMarketplaceView from "@/components/marketplace/IspaiMarketplaceView";
import { useMarketplaceItems } from "@/hooks/useMarketplaceItems";
import MintedStatusBadge from "@/components/marketplace/MintedStatusBadge";

interface Solution {
  id: string;
  title: string;
  channel: string;
  creator: string;
  commercialType: "Sale" | "Lease" | "Collaborate" | "Stake";
  category: string;
  price: number;
  description: string;
  image?: string;
  isMinted?: boolean;
}

const channelSolutionsData: Record<string, Solution[]> = {
  "curt": [
    {
      id: "curt-owners-blueprint",
      title: "CURT Owners Blueprint to Modularization",
      channel: "curt",
      creator: "Construction Users Roundtable",
      commercialType: "Sale",
      category: "Construction Methods",
      price: 3500,
      description: "Authoritative blueprint for offsite construction and modularization for CURT owners.",
      image: kbCurtOwnersBlueprint
    },
    {
      id: "curt-mck-2025-productivity",
      title: "Delivering on Construction Productivity",
      channel: "curt",
      creator: "McKinsey & Company",
      commercialType: "Sale",
      category: "Modularization",
      price: 0,
      description: "Presented at CURT National Conference 2025 (CURT NC25). Free for CURT member companies.",
      image: "/lovable-uploads/9dfe0e93-dac7-409b-bd7d-7c2534cb306e.png"
    },
    {
      id: "curt-002",
      title: "Prefabrication Best Practices Framework",
      channel: "curt",
      creator: "Jim Collins",
      commercialType: "Lease",
      category: "Construction Methods",
      price: 2800,
      description: "Structured framework for prefabrication implementation with case studies and ROI analysis.",
      image: kbPrefabricationFramework
    },
    {
      id: "curt-003",
      title: "Construction Safety Protocol System",
      channel: "curt",
      creator: "Sarah Johnson",
      commercialType: "Collaborate",
      category: "Health & Safety",
      price: 1950,
      description: "Comprehensive safety protocols for modern construction environments.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "curt-004",
      title: "Project Management Excellence Framework",
      channel: "curt",
      creator: "Mike Mosher",
      commercialType: "Sale",
      category: "Project Management",
      price: 4200,
      description: "Specialized knowledge base for construction project management optimization.",
      image: kbProjectManagementFramework
    },
    {
      id: "curt-005",
      title: "Green Building Implementation System",
      channel: "curt",
      creator: "Morgan Chen",
      commercialType: "Stake",
      category: "Green Building",
      price: 5800,
      description: "Complete system for implementing sustainable practices in construction projects.",
      image: kbGreenBuildingSystem
    },
    {
      id: "curt-006",
      title: "BIM Integration Toolkit",
      channel: "curt",
      creator: "Trey Fleming",
      commercialType: "Lease",
      category: "Automation",
      price: 3200,
      description: "Tools and methodologies for seamless BIM integration in construction workflows.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "curt-007",
      title: "Engineering & Design Standards",
      channel: "curt",
      creator: "Karen Thompson",
      commercialType: "Sale",
      category: "Engineering & Design",
      price: 2950,
      description: "Comprehensive design standards and engineering best practices for construction.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "curt-008",
      title: "Construction Data Analytics Platform",
      channel: "curt",
      creator: "Jim Collins",
      commercialType: "Collaborate",
      category: "Data Analytics",
      price: 4500,
      description: "Advanced analytics platform for construction project data optimization.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "curt-osb-2014",
      title: "CURT Owners' Safety Blueprint - 2017 (R-807)",
      channel: "curt",
      creator: "Construction Users Roundtable",
      commercialType: "Sale",
      category: "Health & Safety",
      price: 0,
      description: "CURT Owners' Safety Blueprint (R-807).",
      image: kbOwnersSafetyBlueprint
    },
    {
      id: "curt-hs-001",
      title: "OSHA Safety Standards",
      channel: "curt",
      creator: "Sarah Johnson",
      commercialType: "Sale",
      category: "Health & Safety",
      price: 2500,
      description: "Comprehensive safety regulations and compliance requirements for construction sites.",
      image: kbOshaSafetyStandards
    },
    {
      id: "curt-hs-002",
      title: "Personal Protective Equipment (PPE) Guidelines",
      channel: "curt",
      creator: "Mike Mosher",
      commercialType: "Sale",
      category: "Health & Safety",
      price: 1800,
      description: "Standards and best practices for PPE selection and usage in construction environments.",
      image: kbPpeGuidelines
    },
    {
      id: "curt-hs-003",
      title: "Hazardous Material Management",
      channel: "curt",
      creator: "Karen Thompson",
      commercialType: "Lease",
      category: "Health & Safety",
      price: 2200,
      description: "Protocols for handling, storage, and disposal of hazardous materials on construction sites.",
      image: kbHazardousMaterial
    },
    {
      id: "curt-hs-004",
      title: "Emergency Response Procedures",
      channel: "curt",
      creator: "Jim Collins",
      commercialType: "Sale",
      category: "Health & Safety",
      price: 1950,
      description: "Emergency protocols and response plans for construction site incidents and emergencies.",
      image: kbEmergencyResponse
    },
    {
      id: "curt-hs-005",
      title: "Fall Protection Systems",
      channel: "curt",
      creator: "Morgan Chen",
      commercialType: "Sale",
      category: "Health & Safety",
      price: 3100,
      description: "Technical specifications and implementation guides for fall protection equipment and systems.",
      image: kbFallProtection
    },
    {
      id: "curt-hs-006",
      title: "Site Safety Audits",
      channel: "curt",
      creator: "Trey Fleming",
      commercialType: "Collaborate",
      category: "Health & Safety",
      price: 2400,
      description: "Checklists and procedures for conducting comprehensive safety inspections on construction sites.",
      image: kbSafetyAudits
    },
    {
      id: "curt-hs-007",
      title: "Worker Safety Training Programs",
      channel: "curt",
      creator: "Sarah Johnson",
      commercialType: "Lease",
      category: "Health & Safety",
      price: 2750,
      description: "Comprehensive training modules and certification programs for construction worker safety.",
      image: kbSafetyTraining
    },
    {
      id: "curt-hs-008",
      title: "Incident Reporting & Investigation",
      channel: "curt",
      creator: "Mike Mosher",
      commercialType: "Sale",
      category: "Health & Safety",
      price: 2100,
      description: "Procedures for documenting and analyzing safety incidents to prevent future occurrences.",
      image: kbIncidentReporting
    },
    {
      id: "curt-hs-sol-001",
      title: "AI-Powered Safety Monitoring System",
      channel: "curt",
      creator: "Morgan Chen",
      commercialType: "Stake",
      category: "Health & Safety",
      price: 8500,
      description: "Real-time hazard detection using computer vision and AI to monitor construction site safety.",
      image: solAiSafetyMonitoring
    },
    {
      id: "curt-hs-sol-002",
      title: "Wearable Safety Technology",
      channel: "curt",
      creator: "Karen Thompson",
      commercialType: "Collaborate",
      category: "Health & Safety",
      price: 6200,
      description: "Smart helmets and vests with integrated sensors for real-time worker safety monitoring.",
      image: solWearableSafety
    },
    {
      id: "curt-hs-sol-003",
      title: "Digital Safety Management Platform",
      channel: "curt",
      creator: "Jim Collins",
      commercialType: "Sale",
      category: "Health & Safety",
      price: 5500,
      description: "Centralized system for safety documentation, compliance tracking, and incident management.",
      image: solDigitalSafetyPlatform
    },
    {
      id: "curt-hs-sol-004",
      title: "VR Safety Training Simulator",
      channel: "curt",
      creator: "Trey Fleming",
      commercialType: "Lease",
      category: "Health & Safety",
      price: 7800,
      description: "Immersive virtual reality training for high-risk construction scenarios and emergency response.",
      image: solVrSafetyTraining
    }
  ],
  "oil-gas": [
    {
      id: "og-001",
      title: "Advanced Drilling Optimization System",
      channel: "oil-gas",
      creator: "Pete Dumont",
      commercialType: "Sale",
      category: "Upstream",
      price: 8500,
      description: "Comprehensive system for optimizing drilling operations with real-time data analytics and predictive maintenance.",
      image: ogDrillingOptimization
    },
    {
      id: "og-002",
      title: "Pipeline Integrity Management Framework",
      channel: "oil-gas",
      creator: "Bill Lewis",
      commercialType: "Lease",
      category: "Midstream",
      price: 6200,
      description: "Complete framework for monitoring and maintaining pipeline integrity across midstream operations.",
      image: ogPipelineIntegrity
    },
    {
      id: "og-003",
      title: "Reservoir Simulation Toolkit",
      channel: "oil-gas",
      creator: "Jim Ellis",
      commercialType: "Collaborate",
      category: "Upstream",
      price: 7800,
      description: "Advanced toolkit for reservoir modeling and simulation to maximize extraction efficiency.",
      image: ogReservoirSimulation
    },
    {
      id: "og-004",
      title: "Refinery Process Automation",
      channel: "oil-gas",
      creator: "Greg Sizemore",
      commercialType: "Sale",
      category: "Downstream",
      price: 9200,
      description: "State-of-the-art automation systems for refinery operations to optimize throughput and quality.",
      image: ogRefineryAutomation
    },
    {
      id: "og-005",
      title: "Carbon Capture Technology Blueprint",
      channel: "oil-gas",
      creator: "Nathaniel Sizemore",
      commercialType: "Stake",
      category: "Downstream",
      price: 12500,
      description: "Innovative blueprint for implementing carbon capture and storage technologies in refineries.",
      image: ogCarbonCapture
    },
    {
      id: "og-006",
      title: "Upstream Exploration AI Platform",
      channel: "oil-gas",
      creator: "Austin Fowler",
      commercialType: "Sale",
      category: "Upstream",
      price: 11000,
      description: "AI-powered platform for geological analysis and exploration planning.",
      image: ogExplorationAi
    },
    {
      id: "og-007",
      title: "Transportation Fleet Management System",
      channel: "oil-gas",
      creator: "Pete Dumont",
      commercialType: "Lease",
      category: "Midstream",
      price: 5400,
      description: "Comprehensive system for managing transportation fleets and logistics in midstream operations.",
      image: ogTransportationFleet
    },
    {
      id: "og-008",
      title: "Well Completion Optimization",
      channel: "oil-gas",
      creator: "Bill Lewis",
      commercialType: "Collaborate",
      category: "Upstream",
      price: 6800,
      description: "Proven methodologies for optimizing well completion techniques and equipment selection.",
      image: ogWellCompletion
    },
    {
      id: "og-009",
      title: "Downstream Quality Control Framework",
      channel: "oil-gas",
      creator: "Jim Ellis",
      commercialType: "Sale",
      category: "Downstream",
      price: 7500,
      description: "Comprehensive quality control and assurance framework for refining operations.",
      image: ogQualityControl
    },
    {
      id: "og-010",
      title: "Midstream Storage Solutions",
      channel: "oil-gas",
      creator: "Greg Sizemore",
      commercialType: "Stake",
      category: "Midstream",
      price: 8900,
      description: "Innovative storage solutions and inventory management for midstream operations.",
      image: ogStorageSolutions
    }
  ],
  "gsa": [
    {
      id: "gsa-001",
      title: "Federal Procurement Optimization Framework",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Sale",
      category: "Procurement",
      price: 8500,
      description: "Comprehensive framework for streamlining GWAC and MAS procurement processes across federal agencies.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-002",
      title: "Smart Building IoT Integration System",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Real Estate",
      price: 12000,
      description: "IoT-enabled system for federal building optimization, energy management, and occupancy monitoring.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-003",
      title: "Zero Trust Cybersecurity Architecture",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Sale",
      category: "IT & Cybersecurity",
      price: 15000,
      description: "Comprehensive zero trust security framework for federal IT systems and cloud infrastructure.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-004",
      title: "Federal Fleet Electrification Roadmap",
      channel: "gsa",
      creator: "GSA Fleet Management",
      commercialType: "Lease",
      category: "Fleet Management",
      price: 6500,
      description: "Strategic roadmap for transitioning federal vehicle fleets to electric and hybrid vehicles.",
      image: ogTransportationFleet
    },
    {
      id: "gsa-005",
      title: "Supply Chain Resilience Analytics Platform",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Stake",
      category: "Supply Chain",
      price: 13500,
      description: "Advanced analytics platform for monitoring and strengthening federal supply chain resilience.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-006",
      title: "P3 Infrastructure Investment Model",
      channel: "gsa",
      creator: "Office of Management and Budget",
      commercialType: "Collaborate",
      category: "Public-Private Partnerships",
      price: 9800,
      description: "Proven model for structuring and evaluating public-private partnership infrastructure investments.",
      image: kbPrefabricationFramework
    },
    {
      id: "gsa-007",
      title: "Federal Workforce Digital Skills Framework",
      channel: "gsa",
      creator: "GSA Center for Excellence",
      commercialType: "Sale",
      category: "Workforce Management",
      price: 4500,
      description: "Comprehensive framework for upskilling federal workforce in digital technologies and AI literacy.",
      image: kbSafetyTraining
    },
    {
      id: "gsa-008",
      title: "Net Zero Energy Building Standards",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Energy & Sustainability",
      price: 7200,
      description: "Standards and guidelines for achieving net-zero energy performance in federal buildings.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-009",
      title: "Disaster Recovery Continuity System",
      channel: "gsa",
      creator: "Federal Emergency Management Agency",
      commercialType: "Sale",
      category: "Emergency Preparedness",
      price: 11000,
      description: "Comprehensive system for federal agency continuity of operations and disaster recovery planning.",
      image: kbEmergencyResponse
    },
    {
      id: "gsa-010",
      title: "Smart City Infrastructure Toolkit",
      channel: "gsa",
      creator: "GSA Smart Cities Initiative",
      commercialType: "Collaborate",
      category: "Smart Cities",
      price: 10500,
      description: "Toolkit for implementing smart city technologies in federal facilities and urban planning.",
      image: ogExplorationAi
    },
    {
      id: "gsa-011",
      title: "Grant Management Compliance Framework",
      channel: "gsa",
      creator: "Department of Health and Human Services",
      commercialType: "Lease",
      category: "Federal Grants",
      price: 5800,
      description: "Comprehensive framework ensuring compliance with federal grant management regulations.",
      image: kbSafetyAudits
    },
    {
      id: "gsa-012",
      title: "Federal Travel Expense Automation",
      channel: "gsa",
      creator: "GSA SmartPay Program",
      commercialType: "Sale",
      category: "Travel Management",
      price: 3900,
      description: "Automated system for managing federal travel expenses and per diem calculations.",
      image: kbIncidentReporting
    },
    {
      id: "gsa-013",
      title: "Vendor Performance Evaluation System",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Stake",
      category: "Vendor Management",
      price: 8200,
      description: "Advanced system for evaluating and tracking federal contractor and vendor performance metrics.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "gsa-014",
      title: "Federal Data Governance Platform",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Sale",
      category: "Data Management",
      price: 14500,
      description: "Enterprise platform for federal data governance, quality management, and interagency data sharing.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-015",
      title: "AI Adoption Guidelines for Federal Agencies",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Collaborate",
      category: "Digital Transformation",
      price: 7800,
      description: "Practical guidelines for responsible AI adoption and implementation across federal agencies.",
      image: ogExplorationAi
    },
    // PBS Domain Solutions (gsa-016 through gsa-045)
    {
      id: "gsa-016",
      title: "Requirements Generation: Floor Plan & Space Requirements",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Design Automation",
      price: 8500,
      description: "Automated requirements generation for federal building floor plans and space allocation.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-017",
      title: "Design Tools: Timing & ROM Cost Generation",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Design Automation",
      price: 6200,
      description: "Tools for automated project timeline and rough order of magnitude cost estimation.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-018",
      title: "Subject Matter Expertise Customization: Security & Historic Preservation",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Design Automation",
      price: 9500,
      description: "Specialized design guidance for security compliance and historic preservation requirements.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "gsa-019",
      title: "Government Standards Integration Framework",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Design Automation",
      price: 7800,
      description: "Comprehensive framework integrating federal design standards and building codes.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-020",
      title: "Site-Specific Constraints Management",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Stake",
      category: "Design Automation",
      price: 8200,
      description: "System for identifying and managing site-specific design constraints and requirements.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-021",
      title: "Space Planning Standards and Guidelines",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Space Planning",
      price: 5500,
      description: "Federal standards for efficient workspace planning and space utilization.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-022",
      title: "Furniture Systems and Ergonomics",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Space Planning",
      price: 4800,
      description: "Guidelines for ergonomic furniture selection and workplace wellness design.",
      image: kbPrefabricationFramework
    },
    {
      id: "gsa-023",
      title: "Modular Workspace Design Templates",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Space Planning",
      price: 6100,
      description: "Flexible workspace design templates for adaptable federal office environments.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-024",
      title: "ADA Compliance and Accessibility Standards",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Space Planning",
      price: 7200,
      description: "Comprehensive accessibility standards ensuring ADA compliance in federal facilities.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-025",
      title: "Collaborative Space Configuration Tools",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Stake",
      category: "Space Planning",
      price: 5900,
      description: "Tools for designing collaborative workspaces and meeting environments.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-026",
      title: "Energy Performance Analysis Software",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Analysis Tools",
      price: 9800,
      description: "Advanced software for modeling and analyzing building energy performance.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-027",
      title: "Structural Load Calculation Tools",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Analysis Tools",
      price: 6500,
      description: "Precision tools for structural engineering load calculations and analysis.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "gsa-028",
      title: "Daylight and Solar Analysis",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Analysis Tools",
      price: 7100,
      description: "Analysis tools for optimizing natural lighting and solar heat gain in buildings.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-029",
      title: "HVAC System Sizing and Optimization",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Analysis Tools",
      price: 8400,
      description: "Tools for optimal HVAC system design and energy efficiency analysis.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-030",
      title: "Life Cycle Cost Analysis Framework",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Stake",
      category: "Analysis Tools",
      price: 9200,
      description: "Comprehensive framework for life cycle cost analysis of building systems and materials.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-031",
      title: "ROM Cost Estimating Database",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Cost Estimation",
      price: 7600,
      description: "Historical cost database for rough order of magnitude project estimates.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-032",
      title: "Schedule Development Templates",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Cost Estimation",
      price: 4200,
      description: "Project schedule templates for federal construction and renovation projects.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-033",
      title: "Value Engineering Methodologies",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Cost Estimation",
      price: 6800,
      description: "Proven value engineering techniques for cost optimization without compromising quality.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "gsa-034",
      title: "Construction Cost Indices and Trends",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Cost Estimation",
      price: 5400,
      description: "Up-to-date construction cost indices and market trend analysis for budget planning.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-035",
      title: "Project Risk and Contingency Analysis",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Stake",
      category: "Cost Estimation",
      price: 8100,
      description: "Risk assessment tools and contingency planning frameworks for project budgeting.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-036",
      title: "BIM Modeling Standards and Protocols",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Visualization",
      price: 9500,
      description: "Federal BIM standards and protocols for 3D building information modeling.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-037",
      title: "Virtual Reality Walkthroughs",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Visualization",
      price: 11200,
      description: "Immersive VR technology for stakeholder walkthroughs of proposed designs.",
      image: solVrSafetyTraining
    },
    {
      id: "gsa-038",
      title: "3D Rendering and Animation Tools",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Visualization",
      price: 8700,
      description: "Professional 3D rendering and animation tools for design presentations.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-039",
      title: "Digital Twin Development",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Visualization",
      price: 13500,
      description: "Digital twin technology for real-time building performance monitoring and analysis.",
      image: ogExplorationAi
    },
    {
      id: "gsa-040",
      title: "Stakeholder Presentation Templates",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Stake",
      category: "Visualization",
      price: 3800,
      description: "Professional presentation templates for communicating design concepts to stakeholders.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-041",
      title: "Building Performance Metrics Dashboard",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Post-Occupancy",
      price: 10500,
      description: "Real-time dashboard for monitoring building performance metrics and KPIs.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-042",
      title: "Occupant Satisfaction Surveys",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Post-Occupancy",
      price: 4600,
      description: "Survey tools for measuring occupant satisfaction and workspace effectiveness.",
      image: kbSafetyAudits
    },
    {
      id: "gsa-043",
      title: "Energy Consumption Monitoring",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Post-Occupancy",
      price: 8900,
      description: "Advanced monitoring systems for tracking building energy consumption patterns.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-044",
      title: "Space Utilization Analytics",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Post-Occupancy",
      price: 9700,
      description: "Analytics platform for optimizing space utilization based on occupancy data.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-045",
      title: "Maintenance and Operations Feedback Loop",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Stake",
      category: "Post-Occupancy",
      price: 7300,
      description: "Feedback system for continuous improvement of building maintenance and operations.",
      image: kbIncidentReporting
    },
    // GSA Domain Solutions (gsa-046 through gsa-120)
    {
      id: "gsa-046",
      title: "GSA Schedule 70: IT Solutions",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Sale",
      category: "Procurement",
      price: 6500,
      description: "Comprehensive guide to GSA Schedule 70 IT solutions and procurement processes.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-047",
      title: "Multiple Award Schedules (MAS) Program Guide",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Lease",
      category: "Procurement",
      price: 5800,
      description: "Complete guide to navigating the Multiple Award Schedules program.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-048",
      title: "Simplified Acquisition Procedures Handbook",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Collaborate",
      category: "Procurement",
      price: 4200,
      description: "Handbook for streamlined procurement using simplified acquisition procedures.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-049",
      title: "Federal Supply Schedule Best Practices",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Sale",
      category: "Procurement",
      price: 5500,
      description: "Best practices for effective use of Federal Supply Schedules.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-050",
      title: "Contractor Performance Assessment Framework",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Stake",
      category: "Procurement",
      price: 7800,
      description: "Framework for evaluating and documenting contractor performance.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "gsa-051",
      title: "Federal Property Management Standards",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Real Estate",
      price: 8200,
      description: "Standards for managing federal real estate portfolio and properties.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-052",
      title: "Lease Negotiation and Administration Guide",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Real Estate",
      price: 6900,
      description: "Guide to federal lease negotiation strategies and administration processes.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-053",
      title: "Sustainable Building Design Guidelines",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Real Estate",
      price: 9100,
      description: "Guidelines for sustainable and green building design in federal facilities.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-054",
      title: "Historic Property Preservation Protocols",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Real Estate",
      price: 8700,
      description: "Protocols for preserving and maintaining historic federal properties.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "gsa-055",
      title: "Space Utilization Optimization Toolkit",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Stake",
      category: "Real Estate",
      price: 10200,
      description: "Toolkit for optimizing federal office space utilization and efficiency.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-056",
      title: "Cloud First Policy Implementation Guide",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Sale",
      category: "IT & Cybersecurity",
      price: 9800,
      description: "Implementation guide for federal cloud-first strategy and migration.",
      image: ogExplorationAi
    },
    {
      id: "gsa-057",
      title: "Federal Cybersecurity Framework (NIST)",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Lease",
      category: "IT & Cybersecurity",
      price: 11500,
      description: "Comprehensive NIST cybersecurity framework for federal IT systems.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-058",
      title: "AI/ML Integration for Federal Agencies",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Collaborate",
      category: "IT & Cybersecurity",
      price: 12800,
      description: "Guide to integrating artificial intelligence and machine learning in federal operations.",
      image: ogExplorationAi
    },
    {
      id: "gsa-059",
      title: "Data Governance and Privacy Compliance",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Sale",
      category: "IT & Cybersecurity",
      price: 10600,
      description: "Framework for federal data governance and privacy compliance management.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-060",
      title: "IT Modernization Roadmap",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Stake",
      category: "IT & Cybersecurity",
      price: 13200,
      description: "Strategic roadmap for modernizing legacy federal IT systems and infrastructure.",
      image: ogExplorationAi
    },
    {
      id: "gsa-061",
      title: "Federal Vehicle Acquisition Standards",
      channel: "gsa",
      creator: "GSA Fleet Management",
      commercialType: "Sale",
      category: "Fleet Management",
      price: 5400,
      description: "Standards and procedures for federal vehicle acquisition and leasing.",
      image: ogTransportationFleet
    },
    {
      id: "gsa-062",
      title: "Electric Fleet Transition Planning",
      channel: "gsa",
      creator: "GSA Fleet Management",
      commercialType: "Lease",
      category: "Fleet Management",
      price: 7800,
      description: "Planning guide for transitioning federal fleets to electric vehicles.",
      image: ogTransportationFleet
    },
    {
      id: "gsa-063",
      title: "GPS and Telematics Implementation",
      channel: "gsa",
      creator: "GSA Fleet Management",
      commercialType: "Collaborate",
      category: "Fleet Management",
      price: 6200,
      description: "Implementation guide for GPS tracking and telematics in federal fleets.",
      image: ogTransportationFleet
    },
    {
      id: "gsa-064",
      title: "Fleet Cost Optimization Strategies",
      channel: "gsa",
      creator: "GSA Fleet Management",
      commercialType: "Sale",
      category: "Fleet Management",
      price: 5900,
      description: "Strategies for reducing total cost of ownership in federal fleet operations.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-065",
      title: "Vehicle Lifecycle Management Guide",
      channel: "gsa",
      creator: "GSA Fleet Management",
      commercialType: "Stake",
      category: "Fleet Management",
      price: 6700,
      description: "Comprehensive guide to federal vehicle lifecycle management and disposal.",
      image: ogTransportationFleet
    },
    {
      id: "gsa-066",
      title: "Federal Supply Chain Resilience Framework",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Sale",
      category: "Supply Chain",
      price: 11200,
      description: "Framework for building resilient federal supply chains and risk mitigation.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-067",
      title: "Warehouse Operations Best Practices",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Lease",
      category: "Supply Chain",
      price: 7400,
      description: "Best practices for efficient federal warehouse and inventory operations.",
      image: kbPrefabricationFramework
    },
    {
      id: "gsa-068",
      title: "Strategic Sourcing Methodologies",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Collaborate",
      category: "Supply Chain",
      price: 8900,
      description: "Strategic sourcing methodologies for federal procurement excellence.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-069",
      title: "Global Risk Mitigation Strategies",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Sale",
      category: "Supply Chain",
      price: 10100,
      description: "Strategies for mitigating global supply chain risks and disruptions.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-070",
      title: "Vendor Management Excellence",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Stake",
      category: "Supply Chain",
      price: 9300,
      description: "Excellence framework for federal vendor relationship management.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "gsa-071",
      title: "Public-Private Partnership Models",
      channel: "gsa",
      creator: "Office of Management and Budget",
      commercialType: "Sale",
      category: "Public-Private Partnerships",
      price: 12500,
      description: "Models and frameworks for structuring successful public-private partnerships.",
      image: kbPrefabricationFramework
    },
    {
      id: "gsa-072",
      title: "P3 Contract Templates and Compliance",
      channel: "gsa",
      creator: "Office of Management and Budget",
      commercialType: "Lease",
      category: "Public-Private Partnerships",
      price: 8600,
      description: "Contract templates and compliance guidance for P3 infrastructure projects.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-073",
      title: "Risk Allocation Framework",
      channel: "gsa",
      creator: "Office of Management and Budget",
      commercialType: "Collaborate",
      category: "Public-Private Partnerships",
      price: 9800,
      description: "Framework for optimal risk allocation in public-private partnerships.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-074",
      title: "Alternative Funding Mechanisms",
      channel: "gsa",
      creator: "Office of Management and Budget",
      commercialType: "Sale",
      category: "Public-Private Partnerships",
      price: 11400,
      description: "Guide to alternative funding mechanisms for federal infrastructure projects.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-075",
      title: "Performance-Based Contracting Guide",
      channel: "gsa",
      creator: "Office of Management and Budget",
      commercialType: "Stake",
      category: "Public-Private Partnerships",
      price: 10700,
      description: "Guide to performance-based contracting in public-private partnerships.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-076",
      title: "Federal Human Capital Strategy",
      channel: "gsa",
      creator: "GSA Center for Excellence",
      commercialType: "Sale",
      category: "Workforce Management",
      price: 7900,
      description: "Strategic framework for federal workforce planning and talent management.",
      image: kbSafetyTraining
    },
    {
      id: "gsa-077",
      title: "Employee Training and Development Programs",
      channel: "gsa",
      creator: "GSA Center for Excellence",
      commercialType: "Lease",
      category: "Workforce Management",
      price: 6300,
      description: "Comprehensive training and professional development programs for federal employees.",
      image: kbSafetyTraining
    },
    {
      id: "gsa-078",
      title: "DEI Implementation Toolkit",
      channel: "gsa",
      creator: "GSA Center for Excellence",
      commercialType: "Collaborate",
      category: "Workforce Management",
      price: 5700,
      description: "Toolkit for implementing diversity, equity, and inclusion initiatives in federal agencies.",
      image: kbSafetyTraining
    },
    {
      id: "gsa-079",
      title: "Remote Work Policy Framework",
      channel: "gsa",
      creator: "GSA Center for Excellence",
      commercialType: "Sale",
      category: "Workforce Management",
      price: 4800,
      description: "Framework for developing and implementing federal remote work policies.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-080",
      title: "Leadership Development Pipeline",
      channel: "gsa",
      creator: "GSA Center for Excellence",
      commercialType: "Stake",
      category: "Workforce Management",
      price: 8500,
      description: "Leadership development program for cultivating federal workforce leaders.",
      image: kbSafetyTraining
    },
    {
      id: "gsa-081",
      title: "Green Procurement Standards",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Energy & Sustainability",
      price: 6900,
      description: "Standards for environmentally responsible federal procurement practices.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-082",
      title: "Net Zero Energy Building Guidelines",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Lease",
      category: "Energy & Sustainability",
      price: 8700,
      description: "Guidelines for designing and operating net-zero energy federal buildings.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-083",
      title: "Energy Performance Contracting Manual",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Collaborate",
      category: "Energy & Sustainability",
      price: 7500,
      description: "Manual for implementing energy performance contracts in federal facilities.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-084",
      title: "Climate Resilience Assessment Tools",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Sale",
      category: "Energy & Sustainability",
      price: 9600,
      description: "Tools for assessing climate resilience of federal buildings and infrastructure.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-085",
      title: "Renewable Energy Integration Guide",
      channel: "gsa",
      creator: "GSA Public Buildings Service",
      commercialType: "Stake",
      category: "Energy & Sustainability",
      price: 10800,
      description: "Guide to integrating renewable energy systems in federal facilities.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-086",
      title: "Grant Application Process Guide",
      channel: "gsa",
      creator: "Department of Health and Human Services",
      commercialType: "Sale",
      category: "Federal Grants",
      price: 4500,
      description: "Step-by-step guide to the federal grant application process.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-087",
      title: "Federal Stimulus Package Overview",
      channel: "gsa",
      creator: "Department of Health and Human Services",
      commercialType: "Lease",
      category: "Federal Grants",
      price: 3800,
      description: "Overview of federal stimulus packages and funding opportunities.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-088",
      title: "FEMA Disaster Recovery Grants",
      channel: "gsa",
      creator: "Federal Emergency Management Agency",
      commercialType: "Collaborate",
      category: "Federal Grants",
      price: 6200,
      description: "Guide to FEMA disaster recovery grant programs and application procedures.",
      image: kbEmergencyResponse
    },
    {
      id: "gsa-089",
      title: "Cooperative Agreements Framework",
      channel: "gsa",
      creator: "Department of Health and Human Services",
      commercialType: "Sale",
      category: "Federal Grants",
      price: 5100,
      description: "Framework for establishing and managing federal cooperative agreements.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-090",
      title: "Grant Impact Assessment Methodology",
      channel: "gsa",
      creator: "Department of Health and Human Services",
      commercialType: "Stake",
      category: "Federal Grants",
      price: 7400,
      description: "Methodology for assessing and reporting federal grant program impacts.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-091",
      title: "Federal Disaster Response Planning",
      channel: "gsa",
      creator: "Federal Emergency Management Agency",
      commercialType: "Sale",
      category: "Emergency Preparedness",
      price: 9200,
      description: "Comprehensive disaster response planning framework for federal agencies.",
      image: kbEmergencyResponse
    },
    {
      id: "gsa-092",
      title: "Government Resilience Framework",
      channel: "gsa",
      creator: "Federal Emergency Management Agency",
      commercialType: "Lease",
      category: "Emergency Preparedness",
      price: 8100,
      description: "Framework for building resilient federal government operations and services.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-093",
      title: "Emergency Procurement Procedures",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Collaborate",
      category: "Emergency Preparedness",
      price: 6800,
      description: "Expedited procurement procedures for federal emergency response situations.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-094",
      title: "Critical Infrastructure Protection",
      channel: "gsa",
      creator: "Federal Emergency Management Agency",
      commercialType: "Sale",
      category: "Emergency Preparedness",
      price: 11500,
      description: "Framework for protecting critical federal infrastructure from threats and disasters.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-095",
      title: "COOP Planning and Implementation",
      channel: "gsa",
      creator: "Federal Emergency Management Agency",
      commercialType: "Stake",
      category: "Emergency Preparedness",
      price: 10300,
      description: "Continuity of Operations Planning guide for federal agencies.",
      image: kbEmergencyResponse
    },
    {
      id: "gsa-096",
      title: "Federal Travel Regulation (FTR)",
      channel: "gsa",
      creator: "GSA SmartPay Program",
      commercialType: "Sale",
      category: "Travel Management",
      price: 3200,
      description: "Complete reference to Federal Travel Regulation requirements and guidelines.",
      image: kbIncidentReporting
    },
    {
      id: "gsa-097",
      title: "Per Diem Rate Guidelines",
      channel: "gsa",
      creator: "GSA SmartPay Program",
      commercialType: "Lease",
      category: "Travel Management",
      price: 2800,
      description: "Guidelines and rates for federal per diem allowances and reimbursements.",
      image: kbIncidentReporting
    },
    {
      id: "gsa-098",
      title: "Travel Management Services Contract",
      channel: "gsa",
      creator: "GSA SmartPay Program",
      commercialType: "Collaborate",
      category: "Travel Management",
      price: 4600,
      description: "Contract templates and guidelines for federal travel management services.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-099",
      title: "Travel Compliance Audit Checklist",
      channel: "gsa",
      creator: "GSA SmartPay Program",
      commercialType: "Sale",
      category: "Travel Management",
      price: 3500,
      description: "Checklist for auditing compliance with federal travel policies and regulations.",
      image: kbSafetyAudits
    },
    {
      id: "gsa-100",
      title: "Conference Planning Best Practices",
      channel: "gsa",
      creator: "GSA SmartPay Program",
      commercialType: "Stake",
      category: "Travel Management",
      price: 4100,
      description: "Best practices for planning and managing federal conferences and events.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-101",
      title: "Federal Smart City Strategy",
      channel: "gsa",
      creator: "GSA Smart Cities Initiative",
      commercialType: "Sale",
      category: "Smart Cities",
      price: 12800,
      description: "Strategic framework for implementing smart city technologies in federal operations.",
      image: ogExplorationAi
    },
    {
      id: "gsa-102",
      title: "IoT for Public Infrastructure",
      channel: "gsa",
      creator: "GSA Smart Cities Initiative",
      commercialType: "Lease",
      category: "Smart Cities",
      price: 10900,
      description: "Guide to implementing IoT sensors and systems in public infrastructure.",
      image: kbBimIntegrationToolkit
    },
    {
      id: "gsa-103",
      title: "Smart Energy Grid Implementation",
      channel: "gsa",
      creator: "GSA Smart Cities Initiative",
      commercialType: "Collaborate",
      category: "Smart Cities",
      price: 13200,
      description: "Implementation guide for smart energy grids in federal facilities and cities.",
      image: kbGreenBuildingSystem
    },
    {
      id: "gsa-104",
      title: "Digital Identity Management",
      channel: "gsa",
      creator: "GSA Smart Cities Initiative",
      commercialType: "Sale",
      category: "Smart Cities",
      price: 11600,
      description: "Framework for secure digital identity management in smart city environments.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-105",
      title: "Public Transit Modernization Guide",
      channel: "gsa",
      creator: "GSA Smart Cities Initiative",
      commercialType: "Stake",
      category: "Smart Cities",
      price: 14500,
      description: "Guide to modernizing public transit systems with smart technologies.",
      image: ogTransportationFleet
    },
    {
      id: "gsa-106",
      title: "Federal Data Protection Standards",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Sale",
      category: "IT & Cybersecurity",
      price: 10200,
      description: "Standards for protecting federal data assets from cyber threats and breaches.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-107",
      title: "Identity and Access Management (IAM)",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Lease",
      category: "IT & Cybersecurity",
      price: 9400,
      description: "IAM framework for federal systems and applications security.",
      image: kbSafetyProtocolSystem
    },
    {
      id: "gsa-108",
      title: "Continuous Monitoring Framework",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Collaborate",
      category: "IT & Cybersecurity",
      price: 11800,
      description: "Framework for continuous monitoring of federal IT security posture.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-109",
      title: "Cloud Security for Federal Systems",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Sale",
      category: "IT & Cybersecurity",
      price: 12300,
      description: "Security guidelines and controls for federal cloud computing environments.",
      image: ogExplorationAi
    },
    {
      id: "gsa-110",
      title: "Vendor Cyber Risk Assessment",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Stake",
      category: "IT & Cybersecurity",
      price: 9700,
      description: "Framework for assessing cybersecurity risks from federal vendors and contractors.",
      image: kbSafetyAudits
    },
    {
      id: "gsa-111",
      title: "Industry Day Planning Guide",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Sale",
      category: "Vendor Management",
      price: 5600,
      description: "Guide to planning and executing successful federal industry day events.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-112",
      title: "Vendor Certification Requirements",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Lease",
      category: "Vendor Management",
      price: 6400,
      description: "Requirements and procedures for federal vendor certification and registration.",
      image: kbEngineeringDesignStandards
    },
    {
      id: "gsa-113",
      title: "Small Business Set-Aside Programs",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Collaborate",
      category: "Vendor Management",
      price: 4900,
      description: "Guide to federal small business set-aside and socioeconomic programs.",
      image: kbProjectManagementFramework
    },
    {
      id: "gsa-114",
      title: "GSA Schedule Pricing Analysis",
      channel: "gsa",
      creator: "GSA Federal Acquisition Service",
      commercialType: "Sale",
      category: "Vendor Management",
      price: 7200,
      description: "Tools and methods for analyzing GSA Schedule pricing and commercial discounts.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-115",
      title: "Innovation Procurement Models",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Stake",
      category: "Vendor Management",
      price: 8800,
      description: "Innovative procurement models for emerging technologies and agile development.",
      image: ogExplorationAi
    },
    {
      id: "gsa-116",
      title: "Federal Data Sharing Agreements",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Sale",
      category: "Data Management",
      price: 8300,
      description: "Framework and templates for federal interagency data sharing agreements.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-117",
      title: "Open Government Data Initiative",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Lease",
      category: "Data Management",
      price: 6700,
      description: "Initiative guidelines for publishing federal data as open government data.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-118",
      title: "AI-Driven Analytics Platform",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Collaborate",
      category: "Data Management",
      price: 14200,
      description: "AI-powered analytics platform for federal data insights and decision-making.",
      image: ogExplorationAi
    },
    {
      id: "gsa-119",
      title: "Cloud Data Warehousing Solutions",
      channel: "gsa",
      creator: "GSA Technology Transformation Services",
      commercialType: "Sale",
      category: "Data Management",
      price: 13800,
      description: "Cloud-based data warehousing solutions for federal agencies.",
      image: kbConstructionDataAnalytics
    },
    {
      id: "gsa-120",
      title: "GIS and Geospatial Tools",
      channel: "gsa",
      creator: "18F Digital Services",
      commercialType: "Stake",
      category: "Data Management",
      price: 11900,
      description: "Geographic information systems and geospatial analysis tools for federal use.",
      image: kbConstructionDataAnalytics
    }
  ],
  "ispai": []
};

const ChannelSolutions = () => {
  const [searchParams] = useSearchParams();
  const channelParam = searchParams.get("channel");
  const isDemoMode = searchParams.get("demo") === "true";
  
  const [commercialType, setCommercialType] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedCreator, setSelectedCreator] = useState<string>("all");
  const [cart, setCart] = useState<Solution[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewType, setViewType] = useState("grid");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  
  // Fetch live marketplace data (minted items only)
  // If no channel param, fetch all minted items across all channels
  const { data: liveMarketplaceData, isLoading } = useMarketplaceItems(channelParam || undefined);
  
  // Use demo data if in demo mode for specific channels, otherwise use live data
  const availableSolutions = (isDemoMode && channelParam && channelSolutionsData[channelParam]) 
    ? channelSolutionsData[channelParam] || []
    : liveMarketplaceData || [];


  // Render the Quebec-style marketplace for iSPAI channel
  if (channelParam === "ispai") {
    return <IspaiMarketplaceView initialSolutions={availableSolutions} />;
  }

  const categories = ["all", ...new Set(availableSolutions.map(solution => solution.category))];
  const creators = ["all", ...new Set(availableSolutions.map(solution => solution.creator))];

  const channelTitles: Record<string, { title: string, description: string }> = {
    "curt": {
      title: isDemoMode ? "CURT DEMO Marketplace" : "CURT Marketplace",
      description: isDemoMode 
        ? "Browse hypothetical solutions and knowledge bases for demonstration purposes"
        : "Browse minted knowledge bases and solutions"
    },
    "oil-gas": {
      title: isDemoMode ? "Oil & Gas Marketplace" : "Oil & Gas Marketplace",
      description: isDemoMode 
        ? "Browse hypothetical solutions and knowledge bases for demonstration purposes"
        : "Browse minted knowledge bases and solutions"
    },
    "gsa": {
      title: isDemoMode ? "Federal DEMO Marketplace" : "Federal DEMO Marketplace",
      description: isDemoMode 
        ? "Browse hypothetical solutions and knowledge bases for demonstration purposes"
        : "Browse minted knowledge bases and solutions"
    },
    "ispai": {
      title: "iSPAI Marketplace",
      description: "Browse minted knowledge bases and solutions"
    }
  };

  // If no channel param, show unified marketplace
  const { title, description } = channelParam 
    ? (channelTitles[channelParam] || { title: "Marketplace", description: "Browse minted knowledge bases and solutions" })
    : { title: "Marketplace", description: "Browse all minted knowledge bases and solutions across all channels" };

  useEffect(() => {
    const q = searchParams.get("q");
    const id = searchParams.get("id");
    const view = searchParams.get("view");
    if (q) setSearchQuery(q);
    if (id) {
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 3000);
      setTimeout(() => {
        const el = document.querySelector(`[data-solution-id="${id}"]`) as HTMLElement | null;
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
    if (view === "grid") setViewType("grid");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSolutions = availableSolutions.filter((solution) => {
    return (
      (commercialType === "all" || solution.commercialType === commercialType) &&
      (selectedCategory === "all" || solution.category === selectedCategory) &&
      (selectedCreator === "all" || solution.creator === selectedCreator) &&
      (
        solution.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.creator.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  });

  const addToCart = (solution: Solution) => {
    if (!cart.some(item => item.id === solution.id)) {
      setCart([...cart, solution]);
    }
  };

  const removeFromCart = (id: string) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price, 0);
  };

  const getCommercialTypeColor = (type: string) => {
    switch (type) {
      case "Sale":
        return "bg-blue-100 text-blue-800";
      case "Lease":
        return "bg-green-100 text-green-800";
      case "Collaborate":
        return "bg-purple-100 text-purple-800";
      case "Stake":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryColor = (category: string) => {
    if (category.includes("Construction")) return "bg-red-100 text-red-800";
    if (category.includes("Project")) return "bg-blue-100 text-blue-800";
    if (category.includes("Engineering")) return "bg-indigo-100 text-indigo-800";
    if (category.includes("Green")) return "bg-green-100 text-green-800";
    if (category.includes("Automation")) return "bg-purple-100 text-purple-800";
    if (category.includes("Health")) return "bg-orange-100 text-orange-800";
    if (category.includes("Data")) return "bg-cyan-100 text-cyan-800";
    if (category.includes("AI")) return "bg-violet-100 text-violet-800";
    if (category.includes("Machine")) return "bg-fuchsia-100 text-fuchsia-800";
    if (category.includes("Upstream")) return "bg-emerald-100 text-emerald-800";
    if (category.includes("Downstream")) return "bg-amber-100 text-amber-800";
    if (category.includes("Midstream")) return "bg-teal-100 text-teal-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-black">{title}</h1>
            <p className="text-gray-600 mt-2">{description}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-3">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Filters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Commercial Type</label>
                    <Select value={commercialType} onValueChange={setCommercialType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="Sale">Sale</SelectItem>
                        <SelectItem value="Lease">Lease</SelectItem>
                        <SelectItem value="Collaborate">Collaborate</SelectItem>
                        <SelectItem value="Stake">Stake</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <ListFilter className="h-4 w-4" />
                      Category
                    </label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category === "all" ? "All Categories" : category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <CheckSquare className="h-4 w-4" />
                      Creator
                    </label>
                    <Select value={selectedCreator} onValueChange={setSelectedCreator}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Creator" />
                      </SelectTrigger>
                      <SelectContent>
                        {creators.map((creator) => (
                          <SelectItem key={creator} value={creator}>
                            {creator === "all" ? "All Creators" : creator}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4" />
                    Your Cart ({cart.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {cart.length === 0 ? (
                    <p className="text-sm text-gray-500">Your cart is empty</p>
                  ) : (
                    <div className="space-y-4">
                      {cart.map((item) => (
                        <div key={item.id} className="flex justify-between items-center text-sm border-b pb-2">
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <p className="text-gray-600">${item.price.toLocaleString()}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeFromCart(item.id)}>
                            ×
                          </Button>
                        </div>
                      ))}
                      <div className="pt-2 border-t">
                        <div className="flex justify-between font-medium">
                          <span>Total:</span>
                          <span>${getTotalPrice().toLocaleString()}</span>
                        </div>
                        <Button className="w-full mt-4">
                          Checkout
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="md:col-span-9">
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="relative w-full max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search solutions..." 
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant={viewType === "list" ? "default" : "outline"} 
                      onClick={() => setViewType("list")}
                      className="px-3"
                    >
                      List
                    </Button>
                    <Button 
                      variant={viewType === "grid" ? "default" : "outline"} 
                      onClick={() => setViewType("grid")}
                      className="px-3"
                    >
                      Grid
                    </Button>
                  </div>
                </div>

                {viewType === "list" && (
                  <div className="mt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Solution</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Creator</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSolutions.map((solution) => (
                          <TableRow key={solution.id} data-solution-id={solution.id} className={highlightId === solution.id ? "ring-2 ring-primary rounded-md" : undefined}>
                            <TableCell className="font-medium">
                              {solution.title}
                            </TableCell>
                            <TableCell>
                              <MintedStatusBadge isMinted={solution.isMinted ?? !!solution.image} />
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getCategoryColor(solution.category)}>
                                {solution.category}
                              </Badge>
                            </TableCell>
                            <TableCell>{solution.creator}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getCommercialTypeColor(solution.commercialType)}>
                                {solution.commercialType}
                              </Badge>
                            </TableCell>
                            <TableCell>${solution.price.toLocaleString()}</TableCell>
                            <TableCell>
                              <Button 
                                size="sm" 
                                onClick={() => addToCart(solution)}
                                disabled={cart.some(item => item.id === solution.id)}
                              >
                                {cart.some(item => item.id === solution.id) ? 'Added' : 'Add to Cart'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {viewType === "grid" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSolutions.slice().sort((a, b) => Number(!!b.image) - Number(!!a.image)).map((solution) => (
                      <Card key={solution.id} data-solution-id={solution.id} className={`group relative overflow-hidden ${highlightId === solution.id ? "ring-2 ring-primary rounded-md" : ""}`}>
                        {solution.image && (
                          <img
                            src={solution.image}
                            alt={`${solution.title} cover`}
                            className="w-full h-40 object-cover transition-transform duration-300 ease-out hover:scale-150 cursor-zoom-in"
                            loading="lazy"
                          />
                        )}
                        <div className="p-4">
                          <div className="flex flex-wrap gap-2 items-center mb-3">
                            <MintedStatusBadge isMinted={solution.isMinted ?? !!solution.image} />
                            <Badge variant="outline" className={getCategoryColor(solution.category)}>
                              {solution.category}
                            </Badge>
                            <Badge variant="outline" className={`ml-auto ${getCommercialTypeColor(solution.commercialType)}`}>
                              {solution.commercialType}
                            </Badge>
                          </div>
                          <h3 className="font-medium mb-1">{solution.title}</h3>
                          <p className="text-sm mb-4 text-gray-600 line-clamp-2">{solution.description}</p>
                          <div className="flex justify-between items-center">
                            <span className="font-semibold">${solution.price.toLocaleString()}</span>
                            <Button 
                              size="sm" 
                              onClick={() => addToCart(solution)}
                              disabled={cart.some(item => item.id === solution.id)}
                            >
                              {cart.some(item => item.id === solution.id) ? 
                                'Added' : 
                                <PlusCircle className="h-4 w-4" />
                              }
                            </Button>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">Creator: {solution.creator}</div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                <div className="mt-6">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious href="#" />
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink href="#" isActive>1</PaginationLink>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink href="#">2</PaginationLink>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink href="#">3</PaginationLink>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationNext href="#" />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChannelSolutions;
